import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft"
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight"
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft"
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight"
import { IconButton, StackProps, SxProps, Theme, styled, Typography } from "@mui/material"
import { Stack } from "src/shared/components"
import InputAutocomplete from "src/shared/components/input-autocomplete"

export type PaginationProps = {
  pageIndex: number
  pageSize: number
  totalCount?: number
  pageCount?: number
  disabled?: boolean
  onPageChange: (pageIndex: number) => void
  onPageSizeChange?: (pageSize: number) => void
  pageSizeOptions?: number[]
  hidePageSizeOptions?: boolean
  rowsPerPageLabel?: string
  pageOfLabel?: (page: number, pageCount: number) => string
  justifyContent?: StackProps["justifyContent"]
  sx?: SxProps<Theme>
}

const Pagination = ({
  pageIndex,
  pageSize,
  totalCount = 0,
  pageCount: pageCountProp,
  disabled = false,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 30],
  hidePageSizeOptions = false,
  rowsPerPageLabel = "Rows per page",
  pageOfLabel,
  justifyContent = "flex-end",
  sx
}: PaginationProps) => {
  const pageCount = pageCountProp ?? Math.max(1, Math.ceil(totalCount / pageSize))
  const showPageSizeOptions = !hidePageSizeOptions && pageSizeOptions.length > 0
  const paginationOptions = pageSizeOptions.map((option) => ({ value: String(option), label: String(option) }))
  const canPreviousPage = pageIndex > 0
  const canNextPage = pageIndex + 1 < pageCount

  return (
    <Stack
      direction="row"
      justifyContent={justifyContent}
      alignItems="center"
      gap={2}
      my={2}
      sx={{ flexWrap: "wrap", ...sx }}
    >
      {showPageSizeOptions && (
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="body2" sx={{ whiteSpace: "nowrap", fontWeight: 500 }}>
            {rowsPerPageLabel}
          </Typography>
          <InputAutocomplete
            disableClearable
            maxWidth="70px"
            disabled={disabled || !onPageSizeChange}
            getOptionKey={(option) => option?.value}
            getOptionLabel={(option) => option?.label ?? option}
            options={paginationOptions}
            value={paginationOptions.find((option) => option.value === String(pageSize)) as any}
            onChange={(value) => onPageSizeChange?.(Number((value as any)?.value))}
          />
        </Stack>
      )}
      <Stack variant="rowCenter" spacing={1}>
        <Typography variant="body2" sx={{ whiteSpace: "nowrap", fontWeight: 500 }}>
          {pageOfLabel ? pageOfLabel(pageIndex + 1, pageCount) : `Page ${pageIndex + 1} of ${pageCount}`}
        </Typography>

        <Stack direction="row" alignItems="center" spacing={1}>
          <IconBtn onClick={() => onPageChange(0)} disabled={disabled || !canPreviousPage}>
            <KeyboardDoubleArrowLeftIcon sx={{ fontSize: (theme) => theme.typography.body1.fontSize }} />
          </IconBtn>
          <IconBtn onClick={() => onPageChange(pageIndex - 1)} disabled={disabled || !canPreviousPage}>
            <KeyboardArrowLeftIcon sx={{ fontSize: (theme) => theme.typography.body1.fontSize }} />
          </IconBtn>
          <IconBtn onClick={() => onPageChange(pageIndex + 1)} disabled={disabled || !canNextPage}>
            <KeyboardArrowRightIcon sx={{ fontSize: (theme) => theme.typography.body1.fontSize }} />
          </IconBtn>
          <IconBtn onClick={() => onPageChange(pageCount - 1)} disabled={disabled || !canNextPage}>
            <KeyboardDoubleArrowRightIcon sx={{ fontSize: (theme) => theme.typography.body1.fontSize }} />
          </IconBtn>
        </Stack>
      </Stack>
    </Stack>
  )
}

const IconBtn = styled(IconButton)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "4px"
}))

export default Pagination
